import { handleUpdatePatientReturn } from '@/entities/patient-return';
import { useSetState } from '@/shared/lib/customHooks';
import { showMessageError, convertToMoment } from '@/shared/lib';
import dayjs from 'dayjs';
import { useRmaStore } from '@/entities/rma';
import _ from 'lodash';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { DISABLE_MARK_RETURNED_ENUM } from '../../../config/returnStatusCheckbox';
import type { ReturnStatusCheckboxProps } from './returnStatusCheckbox.types';

/**
 * Drives the study return status tab: the received-items checklist, the mark-device-returned switch, its return date and the save. Derives the default checklist from the RMA store's patientReturnConfig, maps actionDisableMarkReturned onto the message explaining why marking is blocked, compares the current values against the stored baselines to expose isChangeData, and resets from the study whenever the config or patientReturn changes.
 *
 * @param data - Study whose patientReturn record the tab edits.
 * @param updateStudyDetail - Callback given the study with the saved patientReturn merged in.
 * @returns The tab state with the change flag, disabled message and the checkbox, submit, switch, date and reset handlers.
 */
export const useReturnStatusCheckbox = ({ data, updateStudyDetail }: ReturnStatusCheckboxProps) => {
  /**
   * The patient-return configuration selected from the RMA store. Read through useRmaStore so the received-items catalogue stays in sync with the store.
   *
   * @returns Patient return configuration object from the RMA store.
   */
  const patientReturnConfig = useRmaStore((s) => s.patientReturnConfig);

  const defaultReceivedItems = useRef<any>([]);
  const defaultReturnDate = useRef<any>(undefined);

  const [state, setState] = useSetState<any>({
    receivedItems: [],
    markDeviceReturned: false,
    isSaving: false,
  });

  /**
   * The default received-items checklist derived from the patient-return configuration. Memoized over patientReturnConfig: orders its receivedItems by position and maps each to a name plus a checkBox flag pre-set to true.
   *
   * @returns Ordered received items with all checkboxes defaulted to checked.
   */
  const receivedItemsRedux = useMemo(() => {
    const { receivedItems } = patientReturnConfig;
    return _.map(_.orderBy(receivedItems, 'position'), (item) => ({
      name: item.name,
      checkBox: true,
    }));
  }, [patientReturnConfig]);

  /**
   * The reason the device cannot be marked as returned, as a user-facing sentence. Memoized switch over patientReturn.actionDisableMarkReturned covering NEW_STUDY, RMA and the ORG_TRANSFER/ADMIN_TRANSFER pair, returning an empty string when no restriction applies.
   *
   * @returns Explanatory message, or an empty string when marking is allowed.
   */
  const disabledMessage = useMemo(() => {
    const actionDisableMarkReturned = data?.patientReturn?.actionDisableMarkReturned;
    switch (actionDisableMarkReturned) {
      case DISABLE_MARK_RETURNED_ENUM.NEW_STUDY: {
        return 'This device is returned and used in a newer study.';
      }
      case DISABLE_MARK_RETURNED_ENUM.RMA: {
        return 'This device is being processed in an RMA ticket.';
      }
      case DISABLE_MARK_RETURNED_ENUM.ORG_TRANSFER:
      case DISABLE_MARK_RETURNED_ENUM.ADMIN_TRANSFER: {
        return 'This device has been transferred to another facility.';
      }
      default: {
        return '';
      }
    }
  }, [data]);

  const patientReturnProps = data?.patientReturn;

  /**
   * Whether the return status form differs from the last persisted values. Compares state.receivedItems and state.returnDate against the defaultReceivedItems/defaultReturnDate refs and the markDeviceReturned flag against the stored patientReturn value.
   *
   * @returns True when any return field has unsaved changes.
   */
  const isChangeData =
    !_.isEqual(state.receivedItems, defaultReceivedItems.current) ||
    Boolean(state.markDeviceReturned) !== Boolean(patientReturnProps?.markDeviceReturned) ||
    !_.isEqual(state.returnDate, defaultReturnDate.current);

  /**
   * Toggles one entry of the device return checklist on the study's return status tab. Clones the checklist, sets checkBox on the item whose name matches and commits the clone, leaving state untouched when no item matches.
   *
   * @param name - Name of the received item being toggled.
   * @param value - New checked state for that item.
   * @returns Nothing.
   */
  const onChangeCheckBox = (name: any, value: any) => {
    const cloneReceivedItems = [...state.receivedItems];
    /**
     * The received-item entry in the copied list whose name matches the toggled checkbox. Located with Array.find so its checkBox flag can be mutated before the array is written back to state.
     *
     * @returns Matching received item, or undefined when the name is unknown.
     */
    const findedItem = cloneReceivedItems.find((item) => item.name === name);
    if (findedItem) {
      findedItem.checkBox = value;
      setState({ receivedItems: cloneReceivedItems });
    }
  };

  /**
   * Saves the study's patient device return record. Sends markDeviceReturned together with the checklist and the ISO return date when returned, or the default checklist and a null date when not, then merges the returned patientReturn into a clone of the study through updateStudyDetail, logging and surfacing errors and always clearing isSaving.
   *
   * @returns Resolves once the patient return has been saved and propagated.
   */
  const onSubmit = async () => {
    try {
      setState({ isSaving: true });
      const input = {
        id: patientReturnProps?.id,
        markDeviceReturned: state.markDeviceReturned,
      };
      if (state.markDeviceReturned) {
        _.assign(input, {
          receivedItems: state.receivedItems,
          returnDate: dayjs(state.returnDate).toISOString(),
        });
      } else {
        _.assign(input, { receivedItems: receivedItemsRedux, returnDate: null });
      }

      const patientReturn = await handleUpdatePatientReturn(input);
      const clonedData = _.cloneDeep(data);
      const updatedData = {
        ...clonedData,
        patientReturn,
      };
      updateStudyDetail(updatedData);
    } catch (error) {
      console.error('Failed to update patient return: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    } finally {
      setState({ isSaving: false });
    }
  };

  /**
   * Flips the mark-device-returned switch of the study return status tab and seeds the matching return date. Switching on takes the DELIVERED entry from the last return label's tracking history, falling back to now, converted into the study timezone, while switching off clears the date.
   *
   * @returns Nothing.
   */
  const onChangeSwitch = () => {
    const markDeviceReturned = !state.markDeviceReturned;
    const deliveredDate = _.find(data?.patientReturn?.lastReturnLabel?.tracking?.trackingHistory, {
      status: 'DELIVERED',
    })?.statusDate;
    const returnDate = markDeviceReturned
      ? convertToMoment({
          isoDate: deliveredDate || dayjs().toISOString(),
          timezone: data.timezone,
        })
      : null;
    setState({
      markDeviceReturned,
      returnDate,
    });
  };

  /**
   * Records the device return date chosen on the study's return status tab. Merges the date into state as returnDate.
   *
   * @param date - Return date selected in the picker.
   * @returns Nothing.
   */
  const onChangeDate = (date: any) => {
    setState({ returnDate: date });
  };

  /**
   * Restores the return status checklist and switch to the values stored on the study. Rebuilds each entry from the study's saved receivedItems where one matches and from the RMA store defaults otherwise, records the rebuilt list as the change-detection baseline, and applies the saved markDeviceReturned flag and the default return date.
   *
   * @returns Nothing.
   */
  const resetData = (): any => {
    const receivedItems: any = [];

    _.forEach(receivedItemsRedux, (item) => {
      const findedItem = _.find(patientReturnProps?.receivedItems, { name: item.name });
      if (findedItem) {
        receivedItems.push({
          ...findedItem,
        });
      } else {
        receivedItems.push({
          ...item,
        });
      }
    });

    defaultReceivedItems.current = _.cloneDeep(receivedItems);

    setState({
      receivedItems,
      markDeviceReturned: Boolean(patientReturnProps?.markDeviceReturned),
      returnDate: defaultReturnDate.current,
    });
  };

  useLayoutEffect(() => {
    resetData();
  }, [receivedItemsRedux, patientReturnProps]);

  useLayoutEffect(() => {
    if (patientReturnProps?.returnDate) {
      const returnDate = convertToMoment({
        isoDate: patientReturnProps.returnDate,
        timezone: data.timezone,
      });
      setState({ returnDate });
      defaultReturnDate.current = returnDate;
    }
  }, [patientReturnProps]);

  return {
    state,
    isChangeData,
    disabledMessage,
    onChangeCheckBox,
    onSubmit,
    onChangeSwitch,
    onChangeDate,
    resetData,
  };
};
